import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Warning as WarningIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useChatContext } from '../../contexts/ChatContext';
import { useConsole } from '../../contexts/ConsoleContext';
import { FIRST_USE_KEY } from '../chat/ChatInput';
import { fullReset } from '../../services/gameInitialization';
import { uiLogger } from '../../services/uiLogger';

interface DatabaseAdminPanelProps {
  playerId: string; // Not used - component generates new UUIDs
}

export function DatabaseAdminPanel({ playerId: _playerId }: DatabaseAdminPanelProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { clearMessages } = useChatContext();
  const { setSelectedPanel } = useConsole();

  const handleFullWipe = async () => {
    uiLogger.button('DatabaseAdminPanel', 'full_wipe_and_initialize');

    setConfirmDialogOpen(false);
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const result = await fullReset();

    if (result.success) {
      setSuccess('Database fully wiped and initialized successfully!');

      // Clear chat messages and navigate to chat to trigger boot sequence
      clearMessages();

      // Navigate to chat panel after a short delay to allow messages to clear
      setTimeout(() => {
        setSelectedPanel('chat');
        // Clear first-use flag to show "Let's play" prompt again
        localStorage.removeItem(FIRST_USE_KEY);
        // Reload the page to restart the app with fresh data
        window.location.reload();
      }, 1000);
    } else {
      setError(result.error || 'Unknown error occurred');
      setIsLoading(false);
    }
  };

  return (
    <Card
      className="panel"
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <CardContent className="panel-header" sx={{ p: 1.5, pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'error.main',
              fontWeight: 700,
              letterSpacing: 1,
              fontSize: '0.65rem',
            }}
          >
            Game Administration
          </Typography>
          <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Destructive operations for development and testing
        </Typography>
      </CardContent>

      <Box className="panel-scroll" sx={{ px: 1.5, pb: 1.5 }}>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Warning:</strong> This action is destructive and cannot be undone. Only use in development or for testing.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Full Wipe */}
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Wipe & Initialize Database
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Completely wipe the database and reinitialize with fresh data:
          </Typography>

          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Fresh galaxy data (systems, stations, factions)
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Clean player state (ship, cargo, credits reset)
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Empty chat history
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="error"
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={() => setConfirmDialogOpen(true)}
            disabled={isLoading}
            fullWidth
            sx={{ mb: 2 }}
          >
            {isLoading ? 'Processing...' : 'Wipe & Initialize Database'}
          </Button>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ textAlign: 'center' }}>
            This will reload the page and restart the game
          </Typography>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Confirm Database Wipe
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to completely wipe and reinitialize the database?
            <br /><br />
            <strong>This will delete:</strong>
            <ul>
              <li>All galaxy data (systems, stations, factions)</li>
              <li>All player progress and saves</li>
              <li>All chat history</li>
              <li>All game state</li>
            </ul>
            <br />
            The database will be rebuilt from scratch with fresh galaxy data and a new player.
            <br /><br />
            <strong>This action cannot be undone!</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleFullWipe}
            color="error"
            variant="contained"
            autoFocus
          >
            Yes, Wipe Database
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
